import React from 'react';
import useGlobalBlock from '../../hooks/useGlobalBlock';
import HeaderBlock from './HeaderBlock';
import MenuBlock from './MenuBlock';
import FooterBlock from './FooterBlock';
import SidebarFilter from './SidebarFilter';
import { AlertCircle } from 'lucide-react';

/**
 * GlobalBlockRenderer - Rendu dynamique d'un bloc global
 * 
 * Charge le bloc global depuis l'API et affiche le composant approprié
 * 
 * Props:
 * - globalBlockId: ID du bloc global à charger
 * - globalBlockType: Type du bloc (header, menu, footer, sidebar_filter)
 * - globalBlockTitle: Titre du bloc (pour le fallback)
 * 
 * Gère les cas d'erreur et affiche des messages appropriés
 */
const GlobalBlockRenderer = ({ globalBlockId, globalBlockType, globalBlockTitle }) => {
  const { block, loading, error } = useGlobalBlock(globalBlockId);

  // Chargement
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Erreur - Affichage discret pour ne pas casser la page
  if (error || !block) {
    // En production, on peut masquer complètement l'erreur
    if (process.env.NODE_ENV === 'production') {
      console.warn(`Bloc global ${globalBlockTitle || globalBlockId} non disponible`);
      return null;
    }

    // En développement, afficher un message visible
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 my-4">
        <div className="flex items-center gap-2 text-yellow-800">
          <AlertCircle className="h-5 w-5" />
          <div>
            <p className="font-semibold text-sm">
              Bloc global non disponible
            </p>
            <p className="text-xs text-yellow-700">
              {globalBlockTitle || globalBlockId} - {error || 'Introuvable'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier que le bloc est actif
  if (!block.is_active) {
    if (process.env.NODE_ENV === 'development') {
      return (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 my-2">
          <p className="text-xs text-orange-700">
            ℹ️ Bloc global "{globalBlockTitle || block.title}" désactivé
          </p>
        </div>
      );
    }
    return null;
  }

  // Mapper les types aux composants
  const componentMap = {
    header: HeaderBlock,
    menu: MenuBlock,
    footer: FooterBlock,
    sidebar_filter: SidebarFilter,
  };

  const BlockComponent = componentMap[block.type];

  if (!BlockComponent) {
    console.error(`Type de bloc global inconnu: ${block.type}`);
    
    if (process.env.NODE_ENV === 'development') {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
          <div className="flex items-center gap-2 text-red-800">
            <AlertCircle className="h-5 w-5" />
            <div>
              <p className="font-semibold text-sm">Type de bloc global inconnu</p>
              <p className="text-xs text-red-700">
                Type: {block.type} - Vérifier componentMap dans GlobalBlockRenderer
              </p>
            </div>
          </div>
        </div>
      );
    }
    
    return null;
  }

  // Appliquer le padding externe si défini
  const externalPadding = block.content?.external_padding || {};
  const paddingStyle = {
    paddingTop: externalPadding.top ? `${externalPadding.top}px` : '0px',
    paddingRight: externalPadding.right ? `${externalPadding.right}px` : '0px',
    paddingBottom: externalPadding.bottom ? `${externalPadding.bottom}px` : '0px',
    paddingLeft: externalPadding.left ? `${externalPadding.left}px` : '0px',
  };

  // Rendu du composant avec le bloc
  return (
    <div 
      data-global-block-id={block.id} 
      data-global-block-type={block.type}
      className="global-block-wrapper"
      style={paddingStyle}
    >
      <BlockComponent block={block} />
    </div>
  );
};

export default GlobalBlockRenderer;
